import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @extends {Component<{orderPrompt: HTMLElement, remaining: HTMLElement, deliveryDate: HTMLTimeElement}>} */
class HenryDeliveryCountdown extends Component {
  #controller;
  #timer;
  #calendar;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    if (this.dataset.homepage !== 'true') {
      this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    }
    document.addEventListener('visibilitychange', this.#refresh, { signal });
    window.addEventListener('pageshow', this.#refresh, { signal });
    import(this.dataset.calendarUrl).then((calendar) => {
      if (signal.aborted) return;
      this.#calendar = calendar;
      this.#refresh();
    }).catch(() => { if (!signal.aborted) this.hidden = true; });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  // Use the same server-rendered variant eligibility as the purchase controls.
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const sequence = ++this.#selection;
    // Keep the last confirmed offer and its timer steady while the size is checked.
    // Apply the new eligibility only once the latest selection has resolved.
    try {
      const { detail } = await event.promise;
      if (sequence !== this.#selection) return;
      if (!detail?.html || !detail.resource?.available ||
        (detail.productId && String(detail.productId) !== this.dataset.productId && !detail.newProduct)) {
        this.dataset.eligible = 'false';
      } else {
        const replacement = [...detail.html.querySelectorAll('henry-delivery-countdown')]
          .find((element) => element.dataset.blockId === this.dataset.blockId);
        this.dataset.eligible = replacement?.dataset.eligible || 'false';
        if (replacement) this.dataset.productId = replacement.dataset.productId;
      }
    } catch {
      if (sequence === this.#selection) this.dataset.eligible = 'false';
    } finally {
      if (sequence === this.#selection) {
        this.#refresh();
      }
    }
  };

  #refresh = () => {
    clearTimeout(this.#timer);
    if (!this.isConnected || !this.#calendar || this.dataset.eligible !== 'true') {
      this.hidden = true;
      return;
    }
    const now = Date.now();
    const result = this.#calendar.getDeliveryCountdown(now, {
      saturdayDelivery: this.dataset.saturdayDelivery === 'true',
      closedDates: (this.dataset.closedDates || '').split(/[\s,;]+/).filter(Boolean),
    });
    const { orderPrompt, remaining, deliveryDate } = this.refs;
    if (!result || !remaining || !deliveryDate) { this.hidden = true; return; }
    const parts = [];
    if (result.hours) parts.push(`${result.hours} ${result.hours === 1 ? this.dataset.hourOne : this.dataset.hourOther}`);
    if (result.minutes || result.hours) parts.push(`${result.minutes} ${result.minutes === 1 ? this.dataset.minuteOne : this.dataset.minuteOther}`);
    const text = result.showCountdown ? parts.join(' ') || this.dataset.lessThanMinute : '';
    const prompt = result.showCountdown
      ? (parts.length ? this.dataset.orderIn : this.dataset.orderWithin)
      : this.dataset.orderNow;
    if (orderPrompt && orderPrompt.textContent !== prompt) orderPrompt.textContent = prompt;
    if (remaining.textContent !== text) remaining.textContent = text;
    remaining.hidden = !result.showCountdown;
    const language = document.documentElement.lang || 'en-GB';
    const dateText = new Intl.DateTimeFormat(language.startsWith('en') ? 'en-GB' : language, {
      timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'short',
    }).format(new Date(`${result.deliveryDate}T12:00:00Z`)).replace(',', '');
    if (deliveryDate.textContent !== dateText) deliveryDate.textContent = dateText;
    deliveryDate.dateTime = result.deliveryDate;
    this.hidden = false;
    // Align to the next minute and the exact cutoff; suspended tabs catch up on return.
    this.#timer = setTimeout(this.#refresh, Math.min(60000 - now % 60000, result.remainingMilliseconds) + 30);
  };
}

if (!customElements.get('henry-delivery-countdown')) {
  customElements.define('henry-delivery-countdown', HenryDeliveryCountdown);
}
